import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { type JSX, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { OrderCartItem } from '~/api/Order';
import { company } from '~/company/Company';
import { PartiallyPicked } from '~/components/PartiallyPicked/PartiallyPicked';
import { useDateLang } from '~/hooks/useDateLang';
import { useGlobal } from '~/hooks/useGlobal';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { normalizeOrderId } from '~/utils/normalizeOrderId';

import { TrackingMap } from '../TrackingMap';

import { DELIVERY_INDICATORS, TRACKING_MAP_API_URL } from './constants';
import styles from './DeliveringStatus.module.scss';
import type { IDeliveringStatusProps } from './interfaces';

const DeliveringStatus = observer(
  (props: IDeliveringStatusProps): JSX.Element => {
    const { order, className } = props;
    const { t } = useTranslation();
    const { locale } = useDateLang();
    const { timeFormat } = useTimeFormat();

    const { isTablet, isMobile } = useGlobal();
    const isTabletWidth = isTablet || isMobile;

    const formatDate = (date: Date, formatString: string) => {
      return format(date, formatString, {
        timeZone: company.config.timeZone,
        locale,
      });
    };

    const minDate = new Date(order.sla.deliver_by_min);
    const maxDate = new Date(order.sla.deliver_by_max);
    const trackingUrl = order.delivery_tracking_url?.trim();

    const mapInPortal = useMemo(() => {
      const renderMapContent = () => {
        if (order.delivery_tracking_url) {
          return (
            <iframe
              className={styles.trackingIframe}
              frameBorder={0}
              title="Courier tracking"
              src={order.delivery_tracking_url}
            />
          );
        }

        if (order.public_status === 'in_delivery') {
          return (
            <iframe
              className={styles.trackingIframe}
              frameBorder={0}
              title="Courier tracking"
              src={`${TRACKING_MAP_API_URL}?order_id=${normalizeOrderId(
                order.order_id,
              )}`}
            />
          );
        }

        return (
          <TrackingMap
            address={order.address}
            warehouseCoords={{
              lat: order.warehouse_location_latitude,
              lng: order.warehouse_location_longitude,
            }}
            defaultZoom={company.config.map.zoom}
          />
        );
      };

      const mapWrapper = <div className={styles.map}>{renderMapContent()}</div>;

      if (!isTabletWidth) {
        return mapWrapper;
      }
      return createPortal(mapWrapper, document.body);
    }, [order, isTabletWidth]);

    const isPartiallyPicked = useMemo(() => {
      return order?.items.some(
        (i: OrderCartItem) => i.requested_quantity - i.actual_quantity > 0,
      );
    }, [order]);

    const partiallyPickedMessage = isPartiallyPicked &&
      company.showPartiallyPickedInfo && (
        <div className={styles.partiallyPicked__container}>
          {isMobile && <PartiallyPicked />}
          <button
            className={styles.linkToItem}
            onClick={() => {
              const element = document.querySelector(
                '.partiallyDelivered__anchor',
              );
              element?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              });
            }}
          >
            {isMobile ? t('moreInfo') : <PartiallyPicked />}
          </button>
        </div>
      );

    return (
      <section
        className={classes([styles.root, className])}
        data-company={company.name}
      >
        <div className={styles.header}>
          <div className={styles.header_time}>
            <div className={styles.header__line}>
              <div className={styles.title}>
                {t('phrases:estTimeOfDelivery')}{' '}
                <div className={styles.timeWrapper}>
                  <span className={styles.time}>
                    {formatDate(minDate, timeFormat)}
                  </span>
                  <span className={styles.timeDash}>&nbsp;&mdash;&nbsp;</span>
                  <span className={styles.time}>
                    {formatDate(maxDate, timeFormat)}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.date}>
              {formatDate(minDate, 'eee, dd LLLL')}
            </div>
          </div>
          {partiallyPickedMessage}
        </div>
        <div className={styles.mapWrapper}>
          <div className={styles.track}>
            {DELIVERY_INDICATORS.map((indicator, idx) => {
              const actualStatusIndex = DELIVERY_INDICATORS.indexOf(
                order.public_status,
              );
              const isLast = idx === DELIVERY_INDICATORS.length - 1;

              return (
                <div
                  className={classes([
                    styles.item,
                    {
                      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
                      [styles.passed]: actualStatusIndex < idx,
                      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
                      [styles.active]: indicator === order.public_status,
                    },
                  ])}
                  key={indicator}
                >
                  {isLast ? (
                    <div className={styles.iconWrapper}>
                      <div className={styles.icon} />
                    </div>
                  ) : (
                    <div className={styles.icon} />
                  )}
                  <div className={styles.text}>
                    {t(`orderStatus:${indicator}.label`)}
                    {indicator === 'accepted' &&
                      trackingUrl?.includes('wolt') && (
                        <a
                          className={styles.trackingLink}
                          href={trackingUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('expressRealTimeTracking')}
                        </a>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
          {mapInPortal}
        </div>
      </section>
    );
  },
);

export default DeliveringStatus;
